import { FC } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { Input } from '../common/form/Input';
import { SubmitButton } from '../common/form/SubmitButton';
import { FormGroup } from '../common/form/FormGroup';
import { ResponsiveRow } from '../common/form/ResponsiveRow';
import { QcsSwitch } from '../common/form/QcsSwitch';
import { QcsTypography } from '../common/basic/QcsTypography';
import { IntegrationScopeMatrix } from './IntegrationScopeMatrix';
import { IntegrationIpRanges } from './IntegrationIpRanges';
import { getMissingRequirements, ipRangesValidation, scopesValidation } from './integrationUtils';
import { validations } from '../../utils/validations';
import { ApiScopeDto } from '@api/models/apiClient/ApiScopeDto';
import { ApiClientDetailDto } from '@api/models/apiClient/ApiClientDetailDto';
import { ApiClientUpdateDto } from '@api/models/apiClient/ApiClientUpdateDto';

export interface IntegrationFormValues {
  name: string;
  scopes: string[];
  //Off means any IP address may use the API access; the entered ranges are kept, but neither validated nor sent.
  restrictIps: boolean;
  ipRanges: string[];
  //Off means only the company administrators are reminded; the entered contact is kept, but neither validated nor sent.
  notifyContact: boolean;
  contactName: string;
  contactEmail: string;
}

export const toIntegrationFormValues = (detail?: ApiClientDetailDto): IntegrationFormValues => ({
  name: detail?.name ?? '',
  scopes: detail?.scopes ?? [],
  restrictIps: !!detail?.ipRanges.length,
  ipRanges: detail?.ipRanges ?? [],
  notifyContact: !!detail?.contact,
  contactName: detail?.contact?.name ?? '',
  contactEmail: detail?.contact?.email ?? '',
});

export const toApiClientUpdateDto = (values: IntegrationFormValues): ApiClientUpdateDto => ({
  name: values.name.trim(),
  scopes: values.scopes,
  ipRanges: values.restrictIps ? values.ipRanges.map((x) => x.trim()) : [],
  contact: values.notifyContact ? { name: values.contactName.trim() || null, email: values.contactEmail.trim() } : null,
});

export const integrationFormValidation = (t: TFunction, scopeCatalogue: ApiScopeDto[]) => ({
  name: validations.stringRequired(t),
  scopes: scopesValidation(t, scopeCatalogue),
  ipRanges: Yup.array().when('restrictIps', { is: true, then: () => ipRangesValidation(t) }),
  //The e-mail is where the reminders go, so the person can't be without one.
  contactEmail: Yup.string().when('notifyContact', { is: true, then: () => validations.emailRequired(t) }),
});

/** Everything about the API access except what it may access and who gets reminded. It never expires, only its keys do. */
export const IntegrationGeneralFields: FC = () => {
  const { t } = useTranslation();

  return (
    <FormGroup>
      <Input name="name" label={t('integration.apiAccess.name')} maxLength={255} fullWidth required />
    </FormGroup>
  );
};

/** Who else is e-mailed before a key expires - the company administrators always are. Hidden until asked for, as most don't need it. */
export const IntegrationContactFields: FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<IntegrationFormValues>();

  return (
    <FormGroup>
      <QcsSwitch name="notifyContact" label={t('integration.apiAccess.contact.notify')} />
      {values.notifyContact && (
        <>
          <QcsTypography variant="body2">{t('integration.apiAccess.contact.description')}</QcsTypography>
          <ResponsiveRow>
            <Input name="contactName" label={t('integration.apiAccess.contact.name')} maxLength={255} fullWidth />
            <Input name="contactEmail" label={t('integration.apiAccess.contact.email')} type="email" fullWidth required />
          </ResponsiveRow>
        </>
      )}
    </FormGroup>
  );
};

/** All settings at once, for editing an existing API access. */
export const IntegrationFormFields: FC<{ scopeCatalogue: ApiScopeDto[] }> = ({ scopeCatalogue }) => (
  <>
    <IntegrationGeneralFields />

    <FormGroup name="integration.apiAccess.scopes.title">
      <IntegrationScopeMatrix catalogue={scopeCatalogue} />
    </FormGroup>

    <FormGroup name="integration.apiAccess.ipRanges.title">
      <IntegrationIpRanges />
    </FormGroup>

    <IntegrationContactFields />
  </>
);

/** While a checked scope misses its requirement, the form can't go on - the button says so instead of silently failing. */
export const IntegrationSubmitButton: FC<{ scopeCatalogue: ApiScopeDto[]; text?: string }> = ({ scopeCatalogue, text }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<IntegrationFormValues>();
  const hasMissingRequirement = getMissingRequirements(values.scopes, scopeCatalogue).length > 0;

  return <SubmitButton text={text} disabledHint={hasMissingRequirement ? t('integration.apiAccess.scopes.missingRequirementHint') : null} />;
};
